import React from 'react';

import { XomicsApiError } from './api';
import { tx } from './i18n';

// Every view needs the same "is this a known API failure or something unexpected"
// decision, so it lives here rather than being restated in each one.
export function errorMessage(error) {
  if (error instanceof XomicsApiError && error.code) {
    const requestErrors = {
      COLLECTION_WEIGHT_REQUIRED: 'ui.collections.error.parcel_weight',
      COLLECTION_PATIENT_MISMATCH: 'ui.collections.error.patient_mismatch',
      COLLECTION_SAMPLE_MISMATCH: 'ui.collections.error.sample_mismatch',
      COLLECTION_PIPELINE_MISMATCH: 'ui.collections.error.pipeline_mismatch',
      COLLECTION_PIPELINE_INACTIVE: 'ui.collections.error.pipeline_inactive',
      COLLECTION_PIPELINES_REQUIRED: 'ui.collections.error.pipelines_required',
      COLLECTION_UPLOAD_EXTERNAL: 'ui.collections.error.external_upload',
      INVALID_COLLECTION: 'ui.collections.error.invalid_reference',
      KIT_REFERENCE_RETIRED: 'ui.collections.error.kit_reference_retired',

      ACCOUNT_LOCATION_NOT_LABORATORY: 'ui.lab.error.account_location',
      LAB_CATALOGUE_READ_FAILED: 'ui.lab.error.read_failed',
      LAB_PROTOCOL_NOT_FOUND: 'ui.lab.error.protocol_not_found',
      LAB_QUERY_INVALID: 'ui.lab.error.invalid_query',
      DEVICE_PRINT_NOT_CONFIGURED: 'ui.collections.error.printer_not_connected',
      DEVICE_LABEL_SIZE_MISSING: 'ui.collections.error.printer_configuration',
      COLLECTION_PRINTER_UNAVAILABLE: 'ui.collections.error.printer_unavailable',
      COLLECTION_PRINT_UNCONFIRMED: 'ui.collections.error.print_unconfirmed',
      COLLECTION_PRINTER_UNREACHABLE: 'ui.collections.error.printer_unreachable',
      COLLECTION_PRINT_SAVE_FAILED: 'ui.collections.error.print_save_failed',
      HUB_PAGE_INCOMPLETE: 'ui.hub.error.incomplete',
      HUB_PATH_TAKEN: 'ui.hub.error.address_taken',
      HUB_PATH_INVALID: 'ui.hub.error.address_invalid',
      HUB_TITLE_INVALID: 'ui.hub.error.title',
      HUB_NAME_INVALID: 'ui.hub.error.name',
      HUB_LINK_INVALID: 'ui.hub.error.link',
      HUB_SECTION_ONCE: 'ui.hub.error.once',
      HUB_PAGE_FULL: 'ui.hub.error.full',
      HUB_SECTION_FULL: 'ui.hub.error.full',
      ASSET_STORAGE_UNAVAILABLE: 'ui.hub.error.storage',
      MENU_FULL: 'ui.hub.error.full',
      STORE_EMPTY: 'ui.store.error.empty',
      STORE_NOT_EMPTY: 'ui.store.error.not_empty',
      STORE_OFFICE_NOT_YOURS: 'ui.store.error.not_yours',
      STORE_OFFICE_NOT_FOUND: 'ui.store.error.no_store',
      STOCK_QUANTITY_INVALID: 'ui.store.stock.error.quantity',
      STOCK_REASON_INVALID: 'ui.store.stock.error.reason',
      STOCK_REASON_UNKNOWN: 'ui.store.stock.error.reason',
      STOCK_NOTE_TOO_LONG: 'ui.store.stock.error.note',
      PRODUCT_OFF_EVERYWHERE_DENIED: 'ui.store.error.off_everywhere_denied',
      BUNDLE_OFF_EVERYWHERE_DENIED: 'ui.store.error.off_everywhere_denied',
      LOCATION_IS_WEBSITE_DEFAULT: 'ui.store.error.is_default',
      STORE_POLICY_EMPTY: 'ui.policies.error.empty',
      STORE_POLICY_TOO_LONG: 'ui.policies.error.too_long',
      DEVICE_OFFLINE: 'ui.devices.error.offline',
      DEVICE_PRINT_UNCONFIRMED: 'ui.devices.error.print_unconfirmed',
      DEVICE_STATUS_UNREADABLE: 'ui.devices.error.print_unconfirmed',
      DEVICE_NOT_FOUND: 'ui.devices.error.not_found',
      DEVICE_TYPE_NOT_FOUND: 'ui.devices.error.model_unknown',
      DEVICE_LINK_UNAVAILABLE: 'ui.devices.error.link_unavailable',
      DEVICE_SECRET_KEY_MISSING: 'ui.devices.error.wifi_not_saved',
      DEVICE_REQUEST_FAILED: 'ui.devices.error.request_failed',
      LABEL_PRINTER_UNAVAILABLE: 'ui.devices.error.shipping_printer_unavailable',
      LABEL_NOT_PRINTABLE: 'ui.devices.error.label_not_printable',
      COLLECTION_ALREADY_PRINTED: 'ui.collections.error.already_printed',
      COLLECTION_ALREADY_ACTIVATED: 'ui.collections.error.already_activated',
      COLLECTION_NOT_FOUND: 'ui.collections.error.not_found',
      DEVICE_READ_FAILED: 'ui.collections.error.printer_load_failed',
      COLLECTION_HAS_NO_LABELS: 'ui.collections.error.no_labels',
      COLLECTION_PIPELINE_INCOMPATIBLE: 'ui.collections.tubes.error.pipeline',
      COLLECTION_INVALID_TEMPLATE: 'ui.collections.choose_sample_type',
      COLLECTION_TEMPLATE_UNAVAILABLE: 'ui.collections.tubes.error.unavailable',
      COLLECTION_TEMPLATE_CONTENTS_INVALID: 'ui.collections.tubes.error.container',
      COLLECTION_PRODUCT_TEMPLATE_MISSING: 'ui.collections.tubes.error.product_missing',
      COLLECTION_PRODUCT_UNAVAILABLE: 'ui.collections.error.product_unavailable',
      COLLECTION_PRODUCT_DEFAULT_MISSING: 'ui.collections.default_missing',
      COLLECTION_INVALID_SAMPLE_TYPE: 'ui.collections.choose_sample_type',
      COLLECTION_INVALID_COUNT: 'ui.collections.error.invalid_count',
      COLLECTION_INVALID_PIPELINES: 'ui.collections.error.choose_pipelines',
      COLLECTION_DUPLICATE_PIPELINE: 'ui.collections.error.choose_pipelines',
      COLLECTION_REQUEST_CONFLICT: 'ui.collections.error.request_conflict',
      COLLECTION_REQUEST_FAILED: 'ui.collections.error.request_failed',
      COLLECTION_BATCH_INCOMPLETE: 'ui.collections.error.request_failed',
      COLLECTION_CODE_SPACE_EXHAUSTED: 'ui.collections.error.codes_unavailable',
      COLLECTION_INVALID_SELECTION: 'ui.collections.error.print_selection',
      COLLECTION_PRINT_SELECTION_INVALID: 'ui.collections.error.print_selection',
      COLLECTION_LOCATION_INACTIVE: 'ui.collections.error.location_inactive',
      PRODUCT_COLLECTION_OPTIONS_FAILED: 'ui.collections.error.load_defaults_failed',
      PRODUCT_COLLECTION_DEFAULT_FAILED: 'ui.collections.error.save_default_failed',

      STOREFRONT_SITE_REQUIRED: 'ui.storefront.error.site_required',
      STOREFRONT_SITE_NOT_FOUND: 'ui.storefront.error.site_not_found',
      STOREFRONT_INVALID_RANGE: 'ui.storefront.error.invalid_range',
      STOREFRONT_CAMPAIGN_INVALID: 'ui.storefront.error.campaign_invalid',
      STOREFRONT_CAMPAIGN_NOT_FOUND: 'ui.storefront.error.campaign_not_found',
      STOREFRONT_CAMPAIGN_NAME_TAKEN: 'ui.storefront.error.campaign_name_taken',
      STOREFRONT_CHANNEL_UNKNOWN: 'ui.storefront.error.channel_unknown',
      STOREFRONT_CURRENCY_UNKNOWN: 'ui.storefront.error.currency_unknown',
      STOREFRONT_LINK_INVALID: 'ui.storefront.error.link_invalid',
      STOREFRONT_LINK_NOT_FOUND: 'ui.storefront.error.link_not_found',
      STOREFRONT_LINK_CODE_TAKEN: 'ui.storefront.error.link_code_taken',
      STOREFRONT_PATH_REQUIRED: 'ui.storefront.error.path_required',
      STOREFRONT_SESSION_NOT_FOUND: 'ui.storefront.error.session_not_found',
      STOREFRONT_TYPE_REQUIRED: 'ui.storefront.error.type_required',
      STOREFRONT_PROP_INVALID: 'ui.storefront.error.prop_invalid',
      STOREFRONT_SETTING_INVALID: 'ui.storefront.error.setting_invalid',
      STOREFRONT_PATTERN_INVALID: 'ui.storefront.error.pattern_invalid',
      STOREFRONT_ORIGIN_TAKEN: 'ui.storefront.error.origin_taken',
      STOREFRONT_SETTING_NOT_FOUND: 'ui.storefront.error.setting_not_found',
      STOREFRONT_FILTER_INVALID: 'ui.storefront.error.filter_invalid',
      STOREFRONT_RULE_REQUIRED: 'ui.storefront.error.rule_required',
      STOREFRONT_SETTINGS_INVALID: 'ui.storefront.error.settings_invalid',
      STOREFRONT_FAILED: 'ui.storefront.error.failed',

      // What is wrong with a patient's details, written when adding or editing one.
      INVALID_PATIENT_NAME: 'ui.patient.edit_error_name',
      PATIENT_NAME_REQUIRED: 'ui.patient.edit_error_name',
      INVALID_PATIENT_EMAIL: 'ui.patient.edit_error_email',
      INVALID_PATIENT_PHONE: 'ui.patient.edit_error_phone',
      INVALID_PATIENT_DATE_OF_BIRTH: 'ui.patient.edit_error_date_of_birth',

      // What is wrong with the address written in a new shipment's form.
      ADDRESS_COUNTRY_INVALID: 'ui.shipments.error.address_country',
      ADDRESS_LINE_MISSING: 'ui.shipments.error.address_line',
      ADDRESS_TOWN_MISSING: 'ui.shipments.error.address_town',
      ADDRESS_FIELD_INVALID: 'ui.shipments.error.address_too_long',
      ADDRESS_POSTCODE_MISSING: 'ui.shipments.error.address_postcode_missing',
      ADDRESS_POSTCODE_INVALID: 'ui.shipments.error.address_postcode_invalid',
      ADDRESS_POSTCODE_UNKNOWN: 'ui.shipments.error.address_postcode_unknown',
      ADDRESS_TOWN_UNKNOWN: 'ui.shipments.error.address_town_unknown',

      ORDER_NOT_FOUND: 'ui.shipments.error.order_not_found',
      ORDER_LOCATION_UNASSIGNED: 'ui.shipments.error.order_location_unassigned',
      ORDER_LOCATION_MISMATCH: 'ui.shipments.error.order_location_mismatch',
      ORDER_HAS_NO_ITEMS: 'ui.shipments.error.order_has_no_items',
      ORDER_ALREADY_LABELED: 'ui.shipments.error.order_already_labeled',
      ORDER_CURRENCY_MIXED: 'ui.shipments.error.order_currency_mixed',
      LOCATION_UNAVAILABLE: 'ui.shipments.error.location_unavailable',
      LOCATION_CURRENCY_MISSING: 'ui.shipments.error.location_currency_missing',
      NO_LOCATION_CARRIERS: 'ui.shipments.error.no_location_carriers',
      SAMPLE_LOCATION_MISMATCH: 'ui.shipments.error.sample_location_mismatch',
      SHIPMENT_NOT_FOUND: 'ui.shipments.error.shipment_not_found',
      PRODUCT_WEIGHT_MISSING: 'ui.shipments.error.product_weight_missing',
      PRODUCT_NOT_SHIPPABLE: 'ui.shipments.error.product_not_shippable',
      PRODUCT_CUSTOMS_MISSING: 'ui.shipments.error.product_customs_missing',
      TEMPLATE_WEIGHT_MISSING: 'ui.shipments.error.template_weight_missing',
      TEMPLATE_CUSTOMS_MISSING: 'ui.shipments.error.template_customs_missing',
      UNKNOWN_PRODUCT: 'ui.shipments.error.unknown_product',
      SHIPMENT_QUANTITY_INVALID: 'ui.shipments.error.quantity_invalid',
      PARCEL_WEIGHT_MISSING: 'ui.shipments.error.parcel_weight_missing',
      PARCEL_DIMENSIONS_MISSING: 'ui.shipments.error.parcel_dimensions_missing',
      PARCEL_DIMENSIONS_INVALID: 'ui.shipments.error.parcel_dimensions_invalid',
      BOX_UNKNOWN: 'ui.shipments.error.box_unknown',
      RETURN_LABELS_INVALID: 'ui.shipments.error.return_labels_invalid',
      RETURN_LABELS_WITHOUT_KIT: 'ui.shipments.error.return_labels_without_kit',
      CONTENTS_MISSING: 'ui.shipments.error.contents_missing',
      RETURN_CONTENTS_MISSING: 'ui.shipments.error.contents_missing',
      QUOTE_REQUIRED: 'ui.shipments.error.quote_required',
      QUOTE_NOT_AVAILABLE: 'ui.shipments.error.quote_not_available',
      QUOTE_PRODUCT_MISSING: 'ui.shipments.error.quote_product_missing',
      CARRIER_LABEL_MISSING: 'ui.shipments.error.carrier_label_missing',
      CARRIER_RETURN_LABEL_MISSING: 'ui.shipments.error.carrier_return_label_missing',
      CARRIER_NOT_CONFIGURED: 'ui.shipments.error.carrier_not_configured',
      DHL_CUSTOMS_MISSING: 'ui.shipments.error.dhl_customs_missing',
      DHL_RETURN_CUSTOMS_MISSING: 'ui.shipments.error.dhl_return_customs_missing',
      DHL_PHONE_MISSING: 'ui.shipments.error.dhl_phone_missing',
      LABEL_NOT_FOUND: 'ui.shipments.error.label_not_found',
      LABEL_STORAGE_MISSING: 'ui.shipments.error.label_storage_missing',
      INVOICE_NOT_FOUND: 'ui.shipments.error.invoice_not_found',

      // A customer's own portal, and the Customers page.
      CUSTOMER_LINK_INVALID: 'ui.customer_portal.error.link_invalid',
      CUSTOMER_LINK_EXPIRED: 'ui.customer_portal.error.link_expired',
      CUSTOMER_SIGN_IN_FAILED: 'ui.customer_portal.error.sign_in_failed',
      CUSTOMER_SIGN_IN: 'ui.customer_portal.error.sign_in',
      CUSTOMER_ACCOUNT_EXISTS: 'ui.customer_portal.error.account_exists',
      CUSTOMER_PASSWORD_INVALID: 'ui.customer_portal.error.password',
      CUSTOMER_EMAIL_INVALID: 'ui.customer_portal.error.email',
      CUSTOMER_FIELD_INVALID: 'ui.customer_portal.error.field',
      CUSTOMER_INACTIVE: 'ui.customer_portal.error.inactive',
      CUSTOMER_FILE_UNREADABLE: 'ui.customer_portal.error.file_unreadable',
      CUSTOMER_FILE_HEADERS: 'ui.customer_portal.error.file_name_column',
      CUSTOMER_FORM_UNAVAILABLE: 'ui.customer_portal.error.form_unavailable',
      CUSTOMER_FILE_EMPTY: 'ui.customer_portal.error.file_empty',
      CUSTOMER_FILE_TOO_LARGE: 'ui.customer_portal.error.file_too_large',
      CUSTOMER_FILE_TOO_LONG: 'ui.customer_portal.error.file_too_long',
      CUSTOMER_INTAKE_NOT_READY: 'ui.customer_portal.error.not_ready',
      CUSTOMER_SAMPLE_TYPE_INVALID: 'ui.customer_portal.error.sample_type',
      CUSTOMER_SAMPLE_TYPE_UNAVAILABLE: 'ui.customer_portal.error.sample_type_unavailable',
      CUSTOMER_WORKFLOW_NOT_FOUND: 'ui.customer_portal.error.workflow',
      CUSTOMER_DOCUMENT_NOT_FOUND: 'ui.customer_portal.error.not_found',
      CUSTOMER_SUBMISSION_NOT_FOUND: 'ui.customer_portal.error.not_found',
      CUSTOMER_INVOICE_NOT_FOUND: 'ui.customer_portal.error.not_found',
      CUSTOMER_SUBMISSION_SENT: 'ui.customer_portal.error.project_sent',
      CUSTOMER_SUBMISSION_WITHDRAWN: 'ui.customer_portal.error.project_withdrawn',
      CUSTOMER_PROJECT_CLOSED: 'ui.customer_portal.error.project_closed',
      CUSTOMER_REPORT_LANGUAGE_INVALID: 'ui.customer_portal.error.report_language',
      CUSTOMER_REPORTS_PEOPLE: 'ui.customer_portal.error.reports_people',
      CUSTOMER_PERSON_INVALID: 'ui.customer_portal.error.person_invalid',
      CUSTOMER_REPORTS_ASKED: 'ui.customer_portal.error.reports_asked',
      CUSTOMER_REPORTS_UNAVAILABLE: 'ui.customer_portal.error.reports_unavailable',
      CUSTOMER_REPORTS_NOT_READY: 'ui.customer_portal.error.reports_not_ready',
      CUSTOMER_REPORT_NOT_READY: 'ui.customer_portal.error.report_not_ready',
      CUSTOMER_REPORT_IN_REVIEW: 'ui.customer_portal.error.report_in_review',
      CUSTOMER_REPORT_HELD: 'ui.customer_portal.error.report_held',
      CUSTOMER_REPORT_UNAVAILABLE: 'ui.customer_portal.error.report_unavailable',
      CUSTOMER_PROJECT_KIND_INVALID: 'ui.customers.error.project_kind',
      CUSTOMER_CONTINUOUS_NO_COUNT: 'ui.customers.error.continuous_no_count',
      CUSTOMER_CONTINUOUS_FIRST_INVOICE: 'ui.customers.error.continuous_first_invoice',
      CUSTOMER_PROJECT_NOT_CONTINUOUS: 'ui.customers.error.not_continuous',
      CUSTOMER_NO_CONTACT: 'ui.customers.error.no_contact',
      CUSTOMER_NO_PROJECT: 'ui.customers.error.no_project',
      CUSTOMER_CURRENCY_INVALID: 'ui.customers.error.currency',
      CUSTOMER_LABORATORY_INVALID: 'ui.customers.error.laboratory_invalid',
      CUSTOMER_INVOICE_EMPTY: 'ui.customers.error.invoice_empty',
      CUSTOMER_IBAN_INVALID: 'ui.customers.error.iban',
      CUSTOMER_BIC_INVALID: 'ui.customers.error.bic',
      CUSTOMER_BANK_CURRENCY_TWICE: 'ui.customers.error.bank_twice',
      CUSTOMER_BANK_ACCOUNT_MISSING: 'ui.customers.error.bank_account',
      CUSTOMER_BILLING_PROFILE_REQUIRED: 'ui.customers.error.billing_profile_required',
      CUSTOMER_BILLING_PROFILE_NOT_FOUND: 'ui.customers.error.billing_profile_not_found',
      CUSTOMER_BILLING_PROFILE_NAME_TAKEN: 'ui.customers.error.billing_profile_name_taken',
      CUSTOMER_INVOICE_PAID: 'ui.customers.error.invoice_paid',
      CUSTOMER_INVOICE_KIND_REQUIRED: 'ui.customers.error.invoice_kind',
      CUSTOMER_PROFORMA_NO_DUE: 'ui.customers.error.proforma_no_due',
      CUSTOMER_PROFORMA_NOT_PAYABLE: 'ui.customers.error.proforma_not_payable',
      CUSTOMER_EMAIL_ELSEWHERE: 'ui.customers.error.email_elsewhere',
      CUSTOMER_ALREADY_IN_PORTAL: 'ui.customers.error.already_in_portal',
      CUSTOMER_RESET_INVALID: 'ui.customer_portal.error.reset_invalid',
      CUSTOMER_RESET_EXPIRED: 'ui.customer_portal.error.reset_expired',
      CUSTOMER_QC_NOT_WAITING: 'ui.customer_portal.error.qc_not_waiting',
      CUSTOMER_QC_ANSWERS_INVALID: 'ui.customer_portal.error.qc_answers',
      CUSTOMER_QC_SAMPLE_UNKNOWN: 'ui.customer_portal.error.qc_answers',
      CUSTOMER_QC_CHANGED: 'ui.customer_portal.error.qc_changed',
      CUSTOMER_QC_NOTE_INVALID: 'ui.customer_portal.error.qc_note',
      CUSTOMER_ATTACHMENT_TYPE: 'ui.customer_portal.error.attachment_type',
      CUSTOMER_ATTACHMENT_CONTENT: 'ui.customer_portal.error.attachment_content',
      CUSTOMER_ATTACHMENT_TOO_LARGE: 'ui.customer_portal.error.attachment_too_large',
      CUSTOMER_ATTACHMENT_EMPTY: 'ui.customer_portal.error.attachment_empty',
      CUSTOMER_ATTACHMENT_NAME_INVALID: 'ui.customer_portal.error.attachment_name',
      CUSTOMER_ATTACHMENTS_FULL: 'ui.customer_portal.error.attachments_full',
      CUSTOMER_ATTACHMENTS_UNAVAILABLE: 'ui.customer_portal.error.attachments_unavailable',
      CUSTOMER_ATTACHMENT_NOT_FOUND: 'ui.customer_portal.error.not_found',
    };
    // A store left without a price its visitors pay in: the office, the
    // product and the currencies are named (the store per office).
    const unpriced = error.code === 'STORE_PRICES_MISSING' ? error.body?.details : null;
    if (unpriced?.missing?.length > 0) {
      return tx('ui.store.error.prices_missing', {
        office: unpriced.office?.name ?? '', product: unpriced.missing[0].name,
        currencies: unpriced.missing[0].currencies.join(', '),
      });
    }
    // More taken from a stock than there is: how many there are is said.
    const short = error.code === 'STOCK_NOT_ENOUGH' ? error.body?.details : null;
    if (Number.isSafeInteger(short?.stock)) return tx('ui.store.stock.error.not_enough', { count: short.stock.toLocaleString() });
    // A parcel that holds more than the office has: the first line that is short is named.
    if (Array.isArray(short?.lines) && short.lines.length > 0) {
      const [line] = short.lines;
      return tx('ui.store.stock.error.short_line', {
        count: Number(line.stock).toLocaleString(), product: line.label ? `${line.name} · ${line.label}` : line.name,
      });
    }
    // The same for what an office asks for sending an order.
    const unshipped = error.code === 'STORE_SHIPPING_MISSING' ? error.body?.details : null;
    if (unshipped?.currencies?.length > 0) {
      return tx('ui.store.error.shipping_missing', {
        office: unshipped.office?.name ?? '', currencies: unshipped.currencies.join(', '),
      });
    }
    // An invoice in a currency the chosen billing profile has no bank account in: both are named.
    const billing = error.code === 'CUSTOMER_BILLING_DETAILS_MISSING' ? error.body?.details : null;
    if (typeof billing?.currency === 'string') {
      return tx('ui.customers.profile_no_account', { profile: billing.billingProfile ?? '', currency: billing.currency });
    }
    // More samples in a customer's file than their one-time project asks for: both numbers are said.
    const extra = error.code === 'CUSTOMER_TOO_MANY_SAMPLES' ? error.body?.details : null;
    if (Number.isSafeInteger(extra?.allowed) && Number.isSafeInteger(extra?.found)) {
      return tx('ui.customer_portal.error.too_many_samples_project', { allowed: extra.allowed, found: extra.found });
    }
    // A form without columns its samples need: they are named.
    const missing = error.code === 'CUSTOMER_FILE_HEADERS' ? error.body?.details?.missing : null;
    if (Array.isArray(missing) && missing.length > 0) return tx('ui.customer_portal.error.file_columns_missing', { columns: missing.join(', ') });
    if (Object.hasOwn(requestErrors, error.code)) return tx(requestErrors[error.code]);
    return tx('ui.errors.request_failed_with_code', { code: error.code });
  }
  return tx('ui.errors.request_failed');
}

export function Panel({ eyebrow, title, actions, children }) {
  return (
    <section className="xomics-panel">
      <div className="xomics-panel-heading">
        <div>
          {eyebrow && <p className="xomics-auth-eyebrow">{eyebrow}</p>}
          <h2>{title}</h2>
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

// Label, control and its messages. The control is passed in so each caller keeps
// control of its own input type, but the id/error/hint wiring stays in one place.
export function Field({ id, label, error, hint, children }) {
  return (
    <div className="xomics-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <p id={`${id}-hint`} className="xomics-field-hint">{hint}</p>}
      {error && (
        <p id={`${id}-error`} className="xomics-field-error" role="alert">{error}</p>
      )}
    </div>
  );
}

// Builds the aria wiring for a control rendered inside <Field>, so screen readers
// reach the same hint and error text a sighted user sees.
export function fieldAria(id, { error, hint } = {}) {
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`]
    .filter(Boolean)
    .join(' ');
  return {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy || undefined,
  };
}

export function Pager({ offset, count, total, pageSize, busy, onChange }) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + count, total);

  return (
    <>
      <p className="xomics-muted" aria-live="polite">{tx("ui.ui.showing_from_to_of_total_d236a8e4", { from: from, to: to, total: total })}</p>
      <div className="xomics-pager">
        <button
          type="button"
          className="xomics-secondary-button"
          disabled={busy || offset === 0}
          onClick={() => onChange(Math.max(0, offset - pageSize))}
        >
          {tx("ui.ui.previous_50f94286")}
        </button>
        <button
          type="button"
          className="xomics-secondary-button"
          disabled={busy || to >= total}
          onClick={() => onChange(offset + pageSize)}
        >
          {tx("ui.ui.next_bc981983")}
        </button>
      </div>
    </>
  );
}
